// files-drawer.hooks.ts
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { filesApi, type FileListItem } from "../../../api";

/* ── Split filename into stem + extension ── */
export function splitName(name: string): { stem: string; ext: string } {
    const dot = name.lastIndexOf(".");
    if (dot <= 0) return { stem: name, ext: "" };
    return { stem: name.slice(0, dot), ext: name.slice(dot) };
}

/* ── Blob URL cache + hook ── */
const blobCache = new Map<string, string>();

export function useBlobUrl(fileId: string) {
    const [blobUrl, setBlobUrl] = useState<string | null>(
        () => blobCache.get(fileId) ?? null,
    );
    const [loading, setLoading] = useState(!blobUrl);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (blobUrl) return;
        let cancelled = false;

        (async () => {
            try {
                const url = await filesApi.getFileBlobUrl(fileId);
                if (!cancelled) {
                    blobCache.set(fileId, url);
                    setBlobUrl(url);
                }
            } catch {
                if (!cancelled) setError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [fileId, blobUrl]);

    return { blobUrl, loading, error };
}

/* ── Infinite scroll via IntersectionObserver ── */
export function useInfiniteScroll(
    hasMore: boolean,
    loadMore: () => void,
    rootMargin = "200px",
) {
    const sentinelRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const el = sentinelRef.current;
        if (!el || !hasMore) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) loadMore();
            },
            { rootMargin },
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [hasMore, loadMore, rootMargin]);

    return sentinelRef;
}

/* ── Lightbox + options-modal state ── */
export function useFileActions() {
    // Lightbox
    const [lightboxFile, setLightboxFile] = useState<FileLite | null>(null);
    const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

    const handleImageClick = useCallback(
        (file: FileLite, blobUrl: string) => {
            setLightboxFile(file);
            setLightboxUrl(blobUrl);
        },
        [],
    );

    const closeLightbox = useCallback(() => {
        setLightboxFile(null);
        setLightboxUrl(null);
    }, []);

    // Options modal
    const [optionsFile, setOptionsFile] = useState<FileLite | null>(null);
    const [optionsAnchor, setOptionsAnchor] = useState<DOMRect | null>(null);

    const handleDotsClick = useCallback(
        (file: FileLite, rect: DOMRect) => {
            setOptionsFile(file);
            setOptionsAnchor(rect);
        },
        [],
    );

    const handleDownload = useCallback(async () => {
        if (!optionsFile) return;
        try {
            await filesApi.downloadViaProxy(optionsFile.id, optionsFile.name);
        } catch (err) {
            console.error('Download failed:', err);
        }
    }, [optionsFile]);

    const closeOptions = useCallback(() => {
        setOptionsFile(null);
        setOptionsAnchor(null);
    }, []);

    return {
        // Lightbox
        lightboxFile,
        lightboxUrl,
        handleImageClick,
        closeLightbox,
        // Options
        optionsFile,
        optionsAnchor,
        handleDotsClick,
        handleDownload,
        closeOptions,
    };
}

export type NotesDrawerProps = {
    open: boolean;
    onClose: () => void;
    onViewFileInConversation?: (messageId: string, conversationId: string) => void;
    onFileDeleted?: (fileId: string) => void;
};

export type FileLite = {
    id: string;
    name: string;
    kind?: "doc" | "pdf" | "sheet" | "img" | "video" | "audio" | "presentation" | "archive" | "code" | "text" | "other";
    updated_at: string;
    path?: string;
    message_id?: string;
    conversation_id?: string;
    message_content?: string;
    sender_id?: string;
    sender_name?: string;
};

export const formatUpdatedAt = (iso: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();

    const toMidnight = (dt: Date) =>
        new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());

    const dMid = toMidnight(d);
    const today = toMidnight(now);
    const yest = new Date(today);
    yest.setDate(today.getDate() - 1);

    const sameDay = (a: Date, b: Date) => a.getTime() === b.getTime();

    if (sameDay(dMid, today)) return "Today";
    if (sameDay(dMid, yest)) return "Yesterday";

    const sameYear = d.getFullYear() === now.getFullYear();

    return new Intl.DateTimeFormat(undefined, {
        day: "2-digit",
        month: "short",
        ...(sameYear ? {} : { year: "numeric" }),
    }).format(d);
};

const getFileKind = (fileName: string, fileType?: string): NonNullable<FileLite["kind"]> => {
    const lower = fileName.toLowerCase();
    const type = fileType?.toLowerCase() || "";

    if (type.includes("pdf") || lower.endsWith(".pdf")) return "pdf";
    if (type.includes("word") || lower.endsWith(".doc") || lower.endsWith(".docx")) return "doc";
    if (type.includes("sheet") || type.includes("excel") || lower.endsWith(".xls") || lower.endsWith(".xlsx") || lower.endsWith(".csv")) return "sheet";
    if (type.includes("image") || /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(lower)) return "img";
    if (type.includes("video") || /\.(mp4|mov|avi|mkv|webm|mpeg|mpg|3gp|3g2|flv)$/i.test(lower)) return "video";
    if (type.includes("audio") || /\.(mp3|wav|aac|ogg|flac|m4a)$/i.test(lower)) return "audio";
    if (type.includes("presentation") || type.includes("powerpoint") || lower.endsWith(".ppt") || lower.endsWith(".pptx")) return "presentation";
    if (/\.(zip|rar|7z|tar|gz)$/i.test(lower)) return "archive";
    if (/\.(json|xml|html|md|rtf)$/i.test(lower)) return "text";

    return "other";
};

const transformFileToFileLite = (file: FileListItem): FileLite => {
    const lite: FileLite = {
        id: file.id,
        name: file.name,
        kind: getFileKind(file.name, file.type),
        updated_at: file.updated_at,
    };
    if (file.message_id) lite.message_id = file.message_id;
    if (file.conversation_id) lite.conversation_id = file.conversation_id;
    if (file.message_content) lite.message_content = file.message_content;
    if (file.sender_id) lite.sender_id = file.sender_id;
    if (file.sender_name) lite.sender_name = file.sender_name;
    return lite;
};

export type FileGroup = { label: string; files: FileLite[] };

function groupFilesByTimeBucket(files: FileLite[]): FileGroup[] {
    if (files.length === 0) return [];

    const now = new Date();
    const nowMs = now.getTime();
    const DAY = 24 * 60 * 60 * 1000;
    const WEEK = 7 * DAY;

    const getBucket = (iso: string): string => {
        const diff = nowMs - new Date(iso).getTime();
        if (diff < WEEK) return "Most recent";
        const weeks = Math.floor(diff / WEEK);
        if (weeks === 1) return "1 week ago";
        if (weeks < 4) return `${weeks} weeks ago`;
        const months = Math.floor(diff / (30 * DAY));
        if (months <= 1) return "1 month ago";
        if (months < 12) return `${months} months ago`;
        const years = Math.floor(diff / (365 * DAY));
        if (years <= 1) return "1 year ago";
        return `${years} years ago`;
    };

    const groups: FileGroup[] = [];
    const labelToGroup = new Map<string, FileGroup>();

    for (const f of files) {
        const label = getBucket(f.updated_at);
        let group = labelToGroup.get(label);
        if (!group) {
            group = { label, files: [] };
            labelToGroup.set(label, group);
            groups.push(group);
        }
        group.files.push(f);
    }

    return groups;
}

const PAGE_SIZE = 20;

/**
 * Client-side filter: matches file name against query (case-insensitive substring).
 * Handles extension searches like ".txt" and partial name searches like "coherent".
 */
function matchesSearch(file: FileLite, query: string): boolean {
    return file.name.toLowerCase().includes(query.toLowerCase());
}

/**
 * Should the query trigger a server-side search?
 * - Extension queries (start with ".") → always (e.g. ".pdf")
 * - Otherwise → require at least 2 characters to avoid overly broad results
 */
function shouldSearchServer(trimmed: string): boolean {
    if (!trimmed) return false;
    if (trimmed.startsWith(".")) return trimmed.length >= 2; // ".p" is fine
    return trimmed.length >= 2;
}

// ── LRU search cache (keeps last N queries to avoid re-fetching) ──
type SearchCacheEntry = { files: FileLite[]; cursor: string | null; hasMore: boolean };
const SEARCH_CACHE_MAX = 10;
const searchCache = new Map<string, SearchCacheEntry>();

function cacheGet(key: string): SearchCacheEntry | undefined {
    const entry = searchCache.get(key);
    if (entry) {
        // Move to end (most recently used)
        searchCache.delete(key);
        searchCache.set(key, entry);
    }
    return entry;
}

function cacheSet(key: string, entry: SearchCacheEntry) {
    if (searchCache.has(key)) searchCache.delete(key);
    searchCache.set(key, entry);
    // Evict oldest if over limit
    if (searchCache.size > SEARCH_CACHE_MAX) {
        const oldest = searchCache.keys().next().value;
        if (oldest !== undefined) searchCache.delete(oldest);
    }
}

const SEARCH_PAGE_SIZE = 20;

export const useNotesDrawer = (open: boolean) => {
    const bodyRef = useRef<HTMLDivElement | null>(null);
    const barRef = useRef<HTMLDivElement | null>(null);
    const [sheetTop, setSheetTop] = useState<number>(36);
    const [searchActive, setSearchActive] = useState(false);
    const [allFiles, setAllFiles] = useState<FileLite[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const cursorRef = useRef<string | null>(null);

    // ── Search state ──
    const [searchQuery, setSearchQuery] = useState("");
    const [serverResults, setServerResults] = useState<FileLite[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchHasMore, setSearchHasMore] = useState(false);
    const [searchLoadingMore, setSearchLoadingMore] = useState(false);
    const searchCursorRef = useRef<string | null>(null);
    const searchTimerRef = useRef<number | null>(null);
    // AbortController to cancel in-flight search requests
    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        const measure = () => {
            if (!bodyRef.current || !barRef.current) return;
            const bodyRect = bodyRef.current.getBoundingClientRect();
            const barRect = barRef.current.getBoundingClientRect();
            setSheetTop(barRect.bottom - bodyRect.top);
        };

        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [open]);

    useEffect(() => {
        if (!open) {
            setSearchActive(false);
            setSearchQuery("");
            setServerResults([]);
            setSearchLoading(false);
            setSearchHasMore(false);
            setSearchLoadingMore(false);
            searchCursorRef.current = null;
        }
    }, [open]);

    // Fetch first page when drawer opens
    useEffect(() => {
        if (!open) return;

        const fetchFirstPage = async () => {
            setLoading(true);
            setError(null);
            cursorRef.current = null;
            try {
                const result = await filesApi.listFilesPaginated({ limit: PAGE_SIZE });
                setAllFiles(result.files.map(transformFileToFileLite));
                cursorRef.current = result.next_cursor;
                setHasMore(result.has_more);
            } catch (err) {
                console.error("Failed to fetch files:", err);
                setError("Failed to load files");
                setAllFiles([]);
                setHasMore(false);
            } finally {
                setLoading(false);
            }
        };

        fetchFirstPage();
    }, [open]);

    // Load next page (non-search mode)
    const loadMore = useCallback(async () => {
        if (loadingMore || !hasMore || !cursorRef.current) return;
        setLoadingMore(true);
        try {
            const result = await filesApi.listFilesPaginated({
                cursor: cursorRef.current,
                limit: PAGE_SIZE,
            });
            setAllFiles((prev) => [
                ...prev,
                ...result.files.map(transformFileToFileLite),
            ]);
            cursorRef.current = result.next_cursor;
            setHasMore(result.has_more);
        } catch (err) {
            console.error("Failed to load more files:", err);
        } finally {
            setLoadingMore(false);
        }
    }, [loadingMore, hasMore]);

    // Remove a file from the local list (after successful delete)
    const removeFile = useCallback((fileId: string) => {
        setAllFiles((prev) => prev.filter((f) => f.id !== fileId));
    }, []);

    // ── Core server search (used by both initial search and load-more) ──
    const executeServerSearch = useCallback(async (
        trimmed: string,
        cursor: string | null,
        signal: AbortSignal,
    ): Promise<{ files: FileLite[]; nextCursor: string | null; hasMore: boolean } | null> => {
        try {
            const params: { limit: number; search: string; cursor?: string } = {
                limit: SEARCH_PAGE_SIZE,
                search: trimmed,
            };
            if (cursor) params.cursor = cursor;
            const result = await filesApi.listFilesPaginated(params);
            if (signal.aborted) return null;
            const files = result.files.map(transformFileToFileLite);
            return { files, nextCursor: result.next_cursor, hasMore: result.has_more };
        } catch (err) {
            if (signal.aborted) return null;
            console.error("File search failed:", err);
            return null;
        }
    }, []);

    // ── Search handler (called from SearchBar onSearchChange) ──
    const handleSearchChange = useCallback((value: string) => {
        setSearchQuery(value);
        const trimmed = value.trim();

        // Cancel any pending debounce timer
        if (searchTimerRef.current) {
            window.clearTimeout(searchTimerRef.current);
            searchTimerRef.current = null;
        }

        // Cancel any in-flight request
        if (abortRef.current) {
            abortRef.current.abort();
            abortRef.current = null;
        }

        // Reset search pagination
        searchCursorRef.current = null;
        setSearchHasMore(false);
        setSearchLoadingMore(false);

        if (!trimmed || !shouldSearchServer(trimmed)) {
            setServerResults([]);
            setSearchLoading(false);
            return;
        }

        // Check LRU cache first — instant results, no network
        const cached = cacheGet(trimmed);
        if (cached) {
            setServerResults(cached.files);
            searchCursorRef.current = cached.cursor;
            setSearchHasMore(cached.hasMore);
            setSearchLoading(false);
            return;
        }

        // Debounced server search with AbortController
        setSearchLoading(true);
        const controller = new AbortController();
        abortRef.current = controller;

        searchTimerRef.current = window.setTimeout(async () => {
            const result = await executeServerSearch(trimmed, null, controller.signal);
            if (controller.signal.aborted) return;

            if (result) {
                setServerResults(result.files);
                searchCursorRef.current = result.nextCursor;
                setSearchHasMore(result.hasMore);
                // Store in cache
                cacheSet(trimmed, {
                    files: result.files,
                    cursor: result.nextCursor,
                    hasMore: result.hasMore,
                });
            } else {
                setServerResults([]);
                setSearchHasMore(false);
            }
            setSearchLoading(false);
        }, 300);
    }, [executeServerSearch]);

    // ── Load more search results (infinite scroll in search mode) ──
    const loadMoreSearch = useCallback(async () => {
        const trimmed = searchQuery.trim();
        if (searchLoadingMore || !searchHasMore || !searchCursorRef.current || !trimmed) return;

        setSearchLoadingMore(true);
        const controller = new AbortController();
        abortRef.current = controller;

        const result = await executeServerSearch(trimmed, searchCursorRef.current, controller.signal);
        if (controller.signal.aborted) return;

        if (result) {
            setServerResults((prev) => [...prev, ...result.files]);
            searchCursorRef.current = result.nextCursor;
            setSearchHasMore(result.hasMore);
            // Update cache with accumulated results
            cacheSet(trimmed, {
                files: [...serverResults, ...result.files],
                cursor: result.nextCursor,
                hasMore: result.hasMore,
            });
        }
        setSearchLoadingMore(false);
    }, [searchQuery, searchLoadingMore, searchHasMore, serverResults, executeServerSearch]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);
            if (abortRef.current) abortRef.current.abort();
        };
    }, []);

    // ── Merged search results: local instant + server (deduped) ──
    const searchResults = useMemo(() => {
        const trimmed = searchQuery.trim();
        if (!trimmed) return [];

        // 1. Instant client-side matches from already-loaded files
        const localMatches = allFiles.filter((f) => matchesSearch(f, trimmed));
        const seenIds = new Set(localMatches.map((f) => f.id));

        // 2. Merge server results (only add files not already in local matches)
        const extra = serverResults.filter((f) => !seenIds.has(f.id));

        return [...localMatches, ...extra];
    }, [searchQuery, allFiles, serverResults]);

    // Group files into time-based buckets
    const fileGroups = groupFilesByTimeBucket(allFiles);

    return {
        bodyRef,
        barRef,
        sheetTop,
        searchActive,
        setSearchActive,
        fileGroups,
        allFiles,
        loading,
        loadingMore,
        hasMore,
        loadMore,
        error,
        removeFile,
        // Search
        searchQuery,
        searchResults,
        searchLoading,
        searchHasMore,
        searchLoadingMore,
        loadMoreSearch,
        handleSearchChange,
    };
};
